import { THEME } from '@Theme';
import { StyleSheet } from 'aphrodite';
import { brandPrimary } from '../../../web/styles/colors';
import { shadowSm } from '../../../web/styles/shadows';

const { spacing, colors, typography, common } = THEME;

export const styles = StyleSheet.create({
	twoColumnContainer: {
		display: 'grid',
		height: '100%',
		gridTemplateColumns: '3fr 2fr',
	},
	editorPanel: {
		overflowY: 'auto',
		padding: spacing.lg,
		gap: 24,
		display: 'flex',
		flexDirection: 'column',
		...common.scrollBar,
	},
	previewPanel: {
		overflowY: 'auto',
		backgroundColor: colors.background.main,
		borderLeft: `1px solid ${colors.border.light}`,
		...common.scrollBar,
	},
	container: {
		padding: spacing.lg,
		maxWidth: 800,
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: spacing.lg,
	},
	title: {
		fontSize: typography.fontSizes.xl,
		fontWeight: 600,
		color: colors.text.main,
	},
	actions: {
		display: 'flex',
		gap: spacing.sm,
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.lg,
		maxWidth: 600,
	},
	avatarUploaderContainer: {
		display: 'flex',
		justifyContent: 'center',
		marginBottom: spacing.md,
	},
	publicAccessContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
	},
	publicAccessToggleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
	},
	publicAccessLabel: {
		fontSize: typography.fontSizes.sm,
		color: colors.text.light,
	},
	// Allowed origins styles for public access
	allowedOriginsContainer: {
		marginTop: spacing.md,
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
		alignItems: 'flex-start',
	},
	subLabel: {
		fontSize: typography.fontSizes.xs,
		fontWeight: 500,
		color: colors.text.light,
		marginBottom: spacing.xs,
	},
	allowedOriginRow: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		width: '100%',
	},
	allowedOriginInput: {
		flex: 1,
	},
	addAllowedOriginLink: {
		paddingLeft: 0,
		paddingRight: 0,
	},
	// Conversation starters form styles
	conversationStartersContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
		alignItems: 'flex-start',
	},
	conversationStarterRow: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		width: '100%',
	},
	conversationStarterInput: {
		flex: 1,
	},
	conversationStarterRemoveButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: spacing.xs,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		':hover': {
			opacity: 0.7,
		},
	},
	addConversationStarterLink: {
		paddingLeft: 0,
		paddingRight: 0,
	},
	formGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	label: {
		fontSize: typography.fontSizes.sm,
		fontWeight: 600,
		color: colors.text.main,
	},
	textArea: {
		minHeight: 120,
		backgroundColor: 'white',
		boxShadow: shadowSm,
		':focus-visible': {
			border: `1px solid ${brandPrimary}`,
		},
	},
	technicalPromptArea: {
		minHeight: 200,
		resize: 'vertical',
	},
	objectivePromptArea: {
		minHeight: 150,
		resize: 'vertical',
	},
	selectorsContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
	},
	selectorRow: {
		display: 'flex',
		gap: spacing.sm,
		alignItems: 'center',
	},
	selectorInput: {
		flex: 1,
	},
	addSelectorButton: {
		alignSelf: 'flex-start',
	},
	errorMessage: {
		color: colors.danger.main,
		fontSize: typography.fontSizes.sm,
		marginTop: spacing.xs,
	},
	infoMessage: {
		color: colors.text.light,
		fontSize: typography.fontSizes.sm,
		marginTop: spacing.xs,
		fontStyle: 'italic',
	},
	variablesContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
	},
	variableRow: {
		display: 'flex',
		gap: spacing.sm,
		alignItems: 'center',
	},
	variableKeyInput: {
		flex: 1,
		maxWidth: 200,
	},
	variableValueInput: {
		flex: 2,
	},
	emptyVariables: {
		color: colors.text.light,
		fontSize: typography.fontSizes.sm,
		fontStyle: 'italic',
		padding: spacing.md,
		backgroundColor: colors.background.light,
		borderRadius: 4,
	},
	previewContainer: {
		marginTop: spacing.md,
		padding: spacing.md,
		backgroundColor: colors.background.light,
		borderRadius: 4,
		border: `1px solid ${colors.border.main}`,
	},
	previewHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: spacing.sm,
	},
	previewTitle: {
		fontSize: typography.fontSizes.sm,
		fontWeight: 600,
		color: colors.text.main,
	},
	previewContent: {
		fontSize: typography.fontSizes.sm,
		color: colors.text.main,
		whiteSpace: 'pre-wrap',
		fontFamily: 'monospace',
		maxHeight: 300,
		overflowY: 'auto',
	},
	highlightedVariable: {
		backgroundColor: colors.primary.light,
		padding: '2px 4px',
		borderRadius: 2,
	},
	// Survey capability section styles
	surveyCapabilityContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
		alignItems: 'flex-start',
	},
	surveyRow: {
		display: 'flex',
		gap: spacing.xs,
		width: '100%',
	},
	surveyRowHeader: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
		flex: 1,
	},
	surveyCombobox: {
		flex: 1,
	},
	surveyDescriptionInput: {
		minHeight: 60,
	},
	addSurveyLink: {
		paddingLeft: 0,
		paddingRight: 0,
	},
	helpText: {
		fontSize: typography.fontSizes.xs,
		color: colors.text.light,
		marginTop: spacing.xs,
		marginBottom: spacing.sm,
	},
});
